import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChannelPill from "@/components/settings/ChannelPill";
import ChannelDialog from "@/components/settings/ChannelDialog";
import ChannelDeleteDialog from "@/components/settings/ChannelDeleteDialog";

const WEBHOOK_CHANNELS = new Set(["WhatsApp", "Email"]);

export default function ChannelsTable() {
  const { hasPerm } = useAuth();
  const [channels, setChannels] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canEdit = hasPerm("catalogue.manage_contact_modes");
  const load = () => api.get("/channels").then((r) => setChannels(r.data));
  useEffect(() => { load(); }, []);

  const toggleActive = async (ch) => {
    const next = !ch.active;
    setChannels((arr) => arr.map((x) => x.id === ch.id ? { ...x, active: next } : x));
    try {
      await api.patch(`/channels/${ch.id}`, { ...ch, active: next });
      toast.success(next ? `${ch.name} re-activated` : `${ch.name} deactivated`);
    } catch (e) {
      setChannels((arr) => arr.map((x) => x.id === ch.id ? { ...x, active: ch.active } : x));
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  return (
    <Card className="border-slate-200" data-testid="channels-table-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3 gap-3 flex-wrap">
        <div>
          <CardTitle className="font-heading text-base">Channels</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">{channels.length} channel{channels.length === 1 ? "" : "s"} configured</p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-channel-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Create channel
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="text-xs uppercase tracking-wider text-slate-500">
              <TableHead>Channel</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {channels.length === 0 && (
              <TableRow><TableCell colSpan={canEdit ? 4 : 3} className="text-center text-slate-400 text-sm py-12">
                No channels configured yet
              </TableCell></TableRow>
            )}
            {channels.map((ch) => {
              const wired = WEBHOOK_CHANNELS.has(ch.name);
              return (
                <TableRow key={ch.id} className={`hover:bg-slate-50 ${!ch.active ? "opacity-60" : ""}`} data-testid={`channel-row-${ch.name}`}>
                  <TableCell>
                    <ChannelPill channel={ch} size="lg" />
                  </TableCell>
                  <TableCell>
                    {wired ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1" data-testid={`webhook-badge-${ch.name}`}>
                        <Link2 className="w-3 h-3" /> Webhook
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Manual</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => toggleActive(ch)}
                        className={`text-xs px-2 py-0.5 rounded-full border transition ${ch.active
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"}`}
                        data-testid={`channel-status-toggle-${ch.name}`}
                        title={ch.active ? "Click to deactivate" : "Click to reactivate"}
                      >
                        {ch.active ? "Active" : "Inactive"}
                      </button>
                    ) : (
                      <Badge variant="outline" className={`text-xs ${ch.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>
                        {ch.active ? "Active" : "Inactive"}
                      </Badge>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditTarget(ch)} className="h-8 px-2 text-slate-600 hover:bg-slate-100" data-testid={`edit-channel-${ch.name}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(ch)} className="h-8 px-2 text-rose-600 hover:bg-rose-50" data-testid={`delete-channel-${ch.name}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      <ChannelDialog
        open={createOpen || !!editTarget}
        onOpenChange={(v) => { if (!v) { setCreateOpen(false); setEditTarget(null); } }}
        channel={editTarget}
        onSaved={load}
      />
      <ChannelDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        channel={deleteTarget}
        onDeleted={load}
      />
    </Card>
  );
}
